import React, { useState, useEffect } from 'react';
import { 
  Users, 
  BookOpen, 
  MessageCircle, 
  Plus, 
  FileText, 
  Calendar,
  Clock,
  CheckCircle,
  X,
  Edit,
  Trash2,
  Send,
  Paperclip,
  Star
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  teacherHiringService, 
  batchContentService, 
  tuitionTeacherService 
} from '../firebase/tuition';
import { useRealtime } from '../contexts/RealtimeContext';

const TeacherBatch = ({ batchId, onClose }) => {
  const { currentUser: user } = useRealtime();
  const [batch, setBatch] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [posts, setPosts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [showCreateDiscussion, setShowCreateDiscussion] = useState(false);

  useEffect(() => {
    if (batchId && user) {
      loadBatchData();
    }
  }, [batchId, user]);

  const loadBatchData = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        setLoading(false);
        return;
      }
      
      // Load batch details
      const batchData = await teacherHiringService.getBatchDetails(batchId);
      setBatch(batchData);
      
      // Load teacher details
      if (batchData.teacherId) {
        const teacherData = await tuitionTeacherService.getTeacher(batchData.teacherId);
        setTeacher(teacherData);
      }
      
      // Load batch content
      const [postsData, assignmentsData, discussionsData] = await Promise.all([
        batchContentService.getBatchPosts(batchId),
        batchContentService.getBatchAssignments(batchId),
        batchContentService.getBatchDiscussions(batchId)
      ]);
      
      setPosts(postsData);
      setAssignments(assignmentsData);
      setDiscussions(discussionsData);
      
    } catch (error) {
      toast.error('Failed to load batch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (postData) => {
    if (!user) {
      toast.error('User not found');
      return;
    }
    
    try {
      const postId = await batchContentService.createBatchPost(batchId, {
        ...postData,
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorRole: user.uid === batch.teacherId ? 'teacher' : 'student'
      });
      
      toast.success('Post created successfully');
      setShowCreatePost(false);
      loadBatchData(); // Reload to show new post
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleCreateAssignment = async (assignmentData) => {
    if (!user) {
      toast.error('User not found');
      return;
    }
    
    try {
      const assignmentId = await batchContentService.createBatchAssignment(batchId, {
        ...assignmentData,
        teacherId: user.uid,
        teacherName: user.displayName || user.email
      });
      
      toast.success('Assignment created successfully');
      setShowCreateAssignment(false);
      loadBatchData(); // Reload to show new assignment
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const handleCreateDiscussion = async (discussionData) => {
    if (!user) {
      toast.error('User not found');
      return;
    }
    
    try {
      const discussionId = await batchContentService.createBatchDiscussion(batchId, {
        ...discussionData,
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorRole: user.uid === batch.teacherId ? 'teacher' : 'student'
      });
      
      toast.success('Discussion started successfully');
      setShowCreateDiscussion(false);
      loadBatchData(); // Reload to show new discussion
    } catch (error) {
      toast.error('Failed to start discussion');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4">Loading batch...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-red-500">Batch not found</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const isTeacher = user && user.uid === batch.teacherId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{batch.batchName}</h2>
              <p className="text-blue-100">
                Teacher: {teacher?.name || 'Loading...'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'posts', label: 'Posts', icon: FileText },
              { id: 'assignments', label: 'Assignments', icon: BookOpen },
              { id: 'discussions', label: 'Discussions', icon: MessageCircle }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={20} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'posts' && (
            <PostsTab
              posts={posts}
              isTeacher={isTeacher}
              onCreatePost={() => setShowCreatePost(true)}
            />
          )}
          
          {activeTab === 'assignments' && (
            <AssignmentsTab
              assignments={assignments}
              isTeacher={isTeacher}
              onCreateAssignment={() => setShowCreateAssignment(true)}
            />
          )}
          
          {activeTab === 'discussions' && (
            <DiscussionsTab
              discussions={discussions}
              isTeacher={isTeacher}
              onCreateDiscussion={() => setShowCreateDiscussion(true)}
            />
          )}
        </div>

        {/* Create Modals */}
        {showCreatePost && (
          <CreatePostModal
            onClose={() => setShowCreatePost(false)}
            onSubmit={handleCreatePost}
          />
        )}

        {showCreateAssignment && (
          <CreateAssignmentModal
            onClose={() => setShowCreateAssignment(false)}
            onSubmit={handleCreateAssignment}
          />
        )}

        {showCreateDiscussion && (
          <CreateDiscussionModal
            onClose={() => setShowCreateDiscussion(false)}
            onSubmit={handleCreateDiscussion}
          />
        )}
      </div>
    </div>
  );
};

// Posts Tab Component
const PostsTab = ({ posts, isTeacher, onCreatePost }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Posts</h3>
        {isTeacher && (
          <button
            onClick={onCreatePost}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Create Post
          </button>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <FileText size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No posts yet</p>
          {isTeacher && (
            <button
              onClick={onCreatePost}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Post
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
};

// Assignments Tab Component
const AssignmentsTab = ({ assignments, isTeacher, onCreateAssignment }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Assignments</h3>
        {isTeacher && (
          <button
            onClick={onCreateAssignment}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Create Assignment
          </button>
        )}
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No assignments yet</p>
          {isTeacher && (
            <button
              onClick={onCreateAssignment}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Create First Assignment
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <AssignmentCard key={assignment.id} assignment={assignment} />
          ))}
        </div>
      )}
    </div>
  );
};

// Discussions Tab Component
const DiscussionsTab = ({ discussions, isTeacher, onCreateDiscussion }) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Discussions</h3>
        <button
          onClick={onCreateDiscussion}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Start Discussion
        </button>
      </div>

      {discussions.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No discussions yet</p>
          <button
            onClick={onCreateDiscussion}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Start First Discussion
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <DiscussionCard key={discussion.id} discussion={discussion} />
          ))}
        </div>
      )}
    </div>
  );
};

// Post Card Component
const PostCard = ({ post }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <FileText size={20} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{post.authorName}</h4>
            <p className="text-sm text-gray-500">
              {new Date(post.createdAt?.toDate()).toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${
          post.authorRole === 'teacher' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {post.authorRole}
        </span>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h3>
      <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
      
      {post.attachments && post.attachments.length > 0 && (
        <div className="mt-4">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Attachments:</h5>
          <div className="space-y-2">
            {post.attachments.map((attachment, index) => (
              <a
                key={index}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800"
              >
                <Paperclip size={16} className="mr-2" />
                {attachment.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Assignment Card Component
const AssignmentCard = ({ assignment }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <BookOpen size={20} className="text-green-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{assignment.teacherName}</h4>
            <p className="text-sm text-gray-500">
              {new Date(assignment.createdAt?.toDate()).toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
          Assignment
        </span>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{assignment.title}</h3>
      <p className="text-gray-700 mb-4">{assignment.description}</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Calendar size={16} className="mr-2" />
          Due: {new Date(assignment.dueDate?.toDate()).toLocaleDateString()}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Star size={16} className="mr-2" />
          Points: {assignment.points || 'N/A'}
        </div>
      </div>
      
      {assignment.attachments && assignment.attachments.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Resources:</h5>
          <div className="space-y-2">
            {assignment.attachments.map((attachment, index) => (
              <a
                key={index}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800"
              >
                <Paperclip size={16} className="mr-2" />
                {attachment.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Discussion Card Component
const DiscussionCard = ({ discussion }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <MessageCircle size={20} className="text-purple-600" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{discussion.authorName}</h4>
            <p className="text-sm text-gray-500">
              {new Date(discussion.createdAt?.toDate()).toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${
          discussion.authorRole === 'teacher' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {discussion.authorRole}
        </span>
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{discussion.title}</h3>
      <p className="text-gray-700">{discussion.description}</p>
    </div>
  );
};

// Create Post Modal
const CreatePostModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    attachments: []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Create Post</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create Post
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Assignment Modal
const CreateAssignmentModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    points: '',
    attachments: []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      dueDate: new Date(formData.dueDate)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Create Assignment</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points
              </label>
              <input
                type="number"
                value={formData.points}
                onChange={(e) => setFormData({...formData, points: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Create Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Discussion Modal
const CreateDiscussionModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Start Discussion</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What would you like to discuss?"
              required
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Start Discussion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherBatch;
